import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import Modal from '../components/Modal';
import Button from '../components/Button';
import SearchableSelect from '../components/SearchableSelect';
import MonthFilter from '../components/MonthFilter';
import TableScrollContainer from '../components/TableScrollContainer';
import { buildMonthOptions, matchesSelectedMonths } from '../utils/monthFilters';
import { formatCurrency, formatCurrencyInput, parseCurrencyInput } from '../utils/currency';
import { formatServiceName } from '../utils/formatServiceName';
import './Receita.css';

function formatPhone(telefone = '') {
  const digits = String(telefone).replace(/\D/g, '').slice(0, 11);

  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

const initialFormData = () => ({
  cliente_id: '',
  servico_id: '',
  valor: '',
  forma_pagamento: 'Dinheiro',
  observacoes: '',
  data_competencia: new Date().toISOString().slice(0, 10),
  data_recebimento: new Date().toISOString().slice(0, 10),
});

function Receita() {
  const [lancamentos, setLancamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editLanc, setEditLanc] = useState(null);
  const [editForm, setEditForm] = useState({
    valor: '',
    forma_pagamento: 'Dinheiro',
    data_competencia: '',
    data_recebimento: '',
    data_lancamento: '',
  });
  const [formData, setFormData] = useState(initialFormData());
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [monthDateValues, setMonthDateValues] = useState([]);

  useEffect(() => {
    fetchData(selectedMonths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonths]);

  const buildMonthQuery = (months = []) => {
    if (!months || months.length === 0) return '';
    return `?meses=${encodeURIComponent(months.join(','))}`;
  };

  const fetchData = async (months = []) => {
    try {
      const [lancResponse, clientResponse, servResponse] = await Promise.all([
        axios.get(`/api/lancamentos-receita${buildMonthQuery(months)}`),
        axios.get('/api/clientes'),
        axios.get('/api/servicos')
      ]);

      setLancamentos(lancResponse.data);
      if (!months || months.length === 0) {
        setMonthDateValues(lancResponse.data.map((item) => item.data_competencia || item.data_lancamento));
      }
      setClientes(clientResponse.data);
      setServicos(servResponse.data);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthOptions = useMemo(
    () => buildMonthOptions(monthDateValues),
    [monthDateValues]
  );

  const lancamentosFiltrados = useMemo(
    () => lancamentos.filter((item) => matchesSelectedMonths(item.data_competencia || item.data_lancamento, selectedMonths)),
    [lancamentos, selectedMonths]
  );

  const getServicoSelecionado = (servicoId) => {
    return servicos.find((servico) => String(servico.id) === String(servicoId));
  };

  const handleServicoChange = (servicoId) => {
    const servicoSelecionado = getServicoSelecionado(servicoId);

    setFormData((prev) => ({
      ...prev,
      servico_id: servicoId,
      valor: servicoSelecionado ? formatCurrencyInput(servicoSelecionado.valor_padrao) : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valorNormalizado = parseCurrencyInput(formData.valor);
    if (!valorNormalizado) {
      alert('Informe um valor válido.');
      return;
    }

    const payload = {
      ...formData,
      valor: valorNormalizado,
    };

    try {
      await axios.post('/api/lancamentos-receita', payload);
      await fetchData();
      setShowModal(false);
      setFormData(initialFormData());
    } catch (error) {
      console.error('Erro ao salvar lançamento:', error);
      alert(error.response?.data?.error || 'Não foi possível salvar o lançamento.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza?')) return;

    try {
      await axios.delete(`/api/lancamentos-receita/${id}`);
      await fetchData();
    } catch (error) {
      console.error('Erro ao deletar lançamento:', error);
    }
  };
function toDateInput(val) {
  if (!val) return '';
  const str = String(val).trim();
  const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

  const handleEditOpen = (lanc) => {
    setEditLanc(lanc);
    const today = new Date().toISOString().slice(0, 10);
    const dataCompetencia = toDateInput(lanc.data_competencia) || toDateInput(lanc.data_lancamento) || today;
    const dataRecebimento = toDateInput(lanc.data_recebimento) || dataCompetencia;
    const dataLancamento = toDateInput(lanc.data_lancamento) || dataCompetencia;

    setEditForm({
      valor: formatCurrencyInput(lanc.valor),
      forma_pagamento: lanc.forma_pagamento || 'Dinheiro',
      data_competencia: dataCompetencia,
      data_recebimento: dataRecebimento,
      data_lancamento: dataLancamento,
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const valorNormalizado = parseCurrencyInput(editForm.valor);
    if (!valorNormalizado) {
      alert('Informe um valor v\u00e1lido.');
      return;
    }
    try {
      await axios.put(`/api/lancamentos-receita/${editLanc.id}`, {
        valor: valorNormalizado,
        forma_pagamento: editForm.forma_pagamento,
        data_competencia: editForm.data_competencia,
        data_recebimento: editForm.data_recebimento,
        data_lancamento: editForm.data_lancamento,
      });
      await fetchData();
      setEditLanc(null);
    } catch (error) {
      console.error('Erro ao editar lan\u00e7amento:', error);
      alert(error.response?.data?.error || 'N\u00e3o foi poss\u00edvel editar o lan\u00e7amento.');
    }
  };
  const calcularComissao = (valor) => Number(valor || 0) * 0.2;

  return (
    <div className="receita">
      <div className="header-section">
        <h1>Receitas</h1>
        <Button
          variant="primary"
          onClick={() => {
            setFormData(initialFormData());
            setShowModal(true);
          }}
        >
          <FiPlus /> Novo Lançamento
        </Button>
      </div>

      <MonthFilter
        title="Filtrar meses"
        options={monthOptions}
        selectedMonths={selectedMonths}
        onChange={setSelectedMonths}
      />

      {loading ? (
        <p className="loading">Carregando...</p>
      ) : (
        <div className="card">
          <TableScrollContainer rows={lancamentosFiltrados.length} className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Telefone</th>
                  <th>Cliente</th>
                  <th>Serviço</th>
                  <th>Valor Bruto</th>
                  <th>Comissão</th>
                  <th>Forma</th>
                  <th>Competência</th>
                  <th>Recebimento</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {lancamentosFiltrados.map((lanc) => (
                  <tr key={lanc.id}>
                    <td><strong>{formatPhone(lanc.telefone)}</strong></td>
                    <td>{lanc.cliente_nome}</td>
                    <td>{formatServiceName(lanc.servico_nome)}</td>
                    <td>{formatCurrency(lanc.valor)}</td>
                    <td className="comissao">{formatCurrency(calcularComissao(lanc.valor))}</td>
                    <td>{lanc.forma_pagamento}</td>
                    <td>{new Date(`${lanc.data_competencia}T00:00:00`).toLocaleDateString()}</td>
                    <td>{new Date(`${lanc.data_recebimento}T00:00:00`).toLocaleDateString()}</td>
                    <td className="actions">
                      <button className="edit-btn" onClick={() => handleEditOpen(lanc)} title="Editar">
                        <FiEdit2 />
                      </button>
                      <button className="delete-btn" onClick={() => handleDelete(lanc.id)} title="Deletar">
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScrollContainer>
        </div>
      )}

      <Modal isOpen={!!editLanc} onClose={() => setEditLanc(null)} title="Editar Lançamento">
        {editLanc && (
          <form onSubmit={handleEditSubmit} className="form">
            <label>
              Valor (R$) *
              <input
                type="text"
                inputMode="decimal"
                required
                value={editForm.valor}
                onChange={(e) => setEditForm({ ...editForm, valor: formatCurrencyInput(e.target.value) })}
                placeholder="0,00"
              />
            </label>

            {editForm.valor && (
              <div className="comissao-info">
                Comissão 20%: <strong>{formatCurrency(Number(parseCurrencyInput(editForm.valor) || 0) * 0.2)}</strong>
              </div>
            )}

            <label>
              Forma de Pagamento
              <select
                value={editForm.forma_pagamento}
                onChange={(e) => setEditForm({ ...editForm, forma_pagamento: e.target.value })}
              >
                <option value="Dinheiro">Dinheiro</option>
                <option value="Cartão de Crédito">Cartão de Crédito</option>
                <option value="PIX">PIX</option>
                <option value="Fiado">Fiado</option>
              </select>
            </label>

            <div className="date-grid">
              <label>
                Data de lançamento
                <input
                  type="date"
                  value={editForm.data_lancamento}
                  onChange={(e) => setEditForm({ ...editForm, data_lancamento: e.target.value })}
                />
              </label>
              <label>
                Data do serviço / competência
                <input
                  type="date"
                  value={editForm.data_competencia}
                  onChange={(e) => setEditForm({ ...editForm, data_competencia: e.target.value })}
                />
              </label>
              <label>
                Data de recebimento
                <input
                  type="date"
                  value={editForm.data_recebimento}
                  onChange={(e) => setEditForm({ ...editForm, data_recebimento: e.target.value })}
                />
              </label>
            </div>

            <div className="form-actions">
              <Button type="submit" variant="primary">Salvar</Button>
              <Button type="button" variant="secondary" onClick={() => setEditLanc(null)}>Cancelar</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Registrar Serviço Realizado">
        <form onSubmit={handleSubmit} className="form">
          <label>
            Cliente *
            <SearchableSelect
              required
              value={formData.cliente_id}
              onChange={(value) => setFormData({ ...formData, cliente_id: value })}
              placeholder="Selecione um cliente"
              searchPlaceholder="Digite nome ou telefone"
              options={clientes.map((cliente) => ({
                value: String(cliente.id),
                label: `${cliente.nome} (${formatPhone(cliente.telefone)})`,
                searchText: `${cliente.nome} ${cliente.telefone}`,
              }))}
            />
          </label>

          <label>
            Serviço *
            <SearchableSelect
              required
              value={formData.servico_id}
              onChange={handleServicoChange}
              placeholder="Selecione um serviço"
              searchPlaceholder="Digite o nome do serviço"
              options={servicos.map((servico) => ({
                value: String(servico.id),
                label: `${formatServiceName(servico.nome)} - ${formatCurrency(servico.valor_padrao)}`,
                searchText: `${formatServiceName(servico.nome)} ${servico.descricao || ''}`,
              }))}
            />
          </label>

          <label>
            Valor (R$) *
            <input
              type="text"
              inputMode="decimal"
              required
              value={formData.valor}
              onChange={(e) => setFormData({ ...formData, valor: formatCurrencyInput(e.target.value) })}
              placeholder="0,00"
            />
          </label>

          <div className="date-grid">
            <label>
              Data do serviço / competência
              <input
                type="date"
                value={formData.data_competencia}
                onChange={(e) => setFormData({ ...formData, data_competencia: e.target.value })}
              />
            </label>
            <label>
              Data de recebimento
              <input
                type="date"
                value={formData.data_recebimento}
                onChange={(e) => setFormData({ ...formData, data_recebimento: e.target.value })}
              />
            </label>
          </div>

          {formData.valor && (
            <div className="comissao-info">
              Comissão 20%: <strong>{formatCurrency(calcularComissao(formData.valor))}</strong>
            </div>
          )}

          <label>
            Forma de Pagamento
            <select
              value={formData.forma_pagamento}
              onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
            >
              <option value="Dinheiro">Dinheiro</option>
              <option value="Cartão de Crédito">Cartão de Crédito</option>
              <option value="PIX">PIX</option>
              <option value="Fiado">Fiado</option>
            </select>
          </label>

          <label>
            Observações
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              placeholder="Anotações adicionais"
              rows="3"
            />
          </label>

          <div className="form-actions">
            <Button type="submit" variant="primary">Registrar</Button>
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Receita;
